import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, matchPath, useLocation } from "react-router-dom";
import * as ROUTES from "../../constants/routes";
import { ReactComponent as BoxLogo } from "../../assets/box-in-logo.svg";
import { ReactComponent as OverviewIcon } from "../../assets/overview-icon.svg";
import { ReactComponent as LabelsIcon } from "../../assets/labels-icon.svg";
import { ReactComponent as OrdersIcon } from "../../assets/orders-icon.svg";
import { ReactComponent as HelpIcon } from "../../assets/helpcenter-icon.svg";
import { ReactComponent as SettingIcon } from "../../assets/settings-icon.svg";
import { ReactComponent as StoreIcon } from "../../assets/connect-store-icon.svg";
import { FiInfo, FiShoppingCart } from "react-icons/fi";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FIREBASE_AUTH } from "../../constants/types";
import {
  getRedirectURL,
  isAllowedRedirect,
  isBlockedWalletAccount,
  missingDetails,
} from "./common/helpers";
import history from "../../History";
import { clearSelectedCheckout } from "../actions/CheckoutsActions";
import { FiPackage } from "react-icons/fi";
import { LuWallet } from "react-icons/lu";
import { getWebToken } from "../actions/ActionHelpers";
import { setApplicationContext } from "../actions/ApplicationContextActions";

export const NavItem = (props) => {
  const location = useLocation();
  let isCurrent = matchPath(location.pathname, {
    path: props.link,
    exact: false,
  });
  if (props.link === ROUTES.ACCOUNT) {
    isCurrent = matchPath(location.pathname, {
      path: props.link,
      exact: false,
    });
  }

  const Component = props.isExternal ? "a" : Link;
  const linkProps = props.isExternal
    ? {
        onClick: () => {
          window.location.href = props.link;
        },
      }
    : {
        to: props.link,
      };

  return (
    <li>
      <Component
        className={`nav-item col-12 d-flex flex-row align-items-center ${isCurrent ? "active" : ""}`}
        {...linkProps}
      >
        <div className="d-flex flex-row align-items-center">
          {props.icon}
          <p className="mb-0">
            {props.name}
            {props.missing && !isCurrent && (
              <OverlayTrigger
                key={"business-details-missing"}
                placement="right"
                overlay={
                  <Tooltip
                    id="business-details-missing-tooltip"
                    className={"business-details-missing-tooltip"}
                  >
                    You are seeing this because you have missing information for
                    your business details
                  </Tooltip>
                }
                delayShow={300}
                delayHide={150}
              >
                <span>
                  <FiInfo className={"warning"} />
                </span>
              </OverlayTrigger>
            )}
          </p>
        </div>
        <div
          className={`counter mx-3  align-items-center justify-content-center ml-auto ${props.count && props.count > 0 ? "d-flex" : "d-none"}`}
        >
          {props.count}
        </div>
      </Component>
    </li>
  );
};

const Sidebar = (props) => {
  const [webToken, setWebToken] = useState(null);
  const { pathname, search } = useLocation();

  const hideWallet = isBlockedWalletAccount(
    props.context?.auth,
    props.userInfo?.email,
  );

  const redirectURL = (route) =>
    getRedirectURL(
      route,
      webToken,
      props.context?.auth,
      props.context?.organizationId,
    );

  useEffect(() => {
    const awaitWebToken = async () => {
      const token = await getWebToken();
      setWebToken(token);
    };
    awaitWebToken();
  }, []);

  const organization = props.organizations?.find(
    (org) => org.id === props.context.organizationId,
  );

  return (
    <div
      className={`sidebar d-none d-xl-flex flex-column col-2 p-0 align-items-center `}
    >
      <a
        href={"https://shipshap.com"}
        className="header d-flex flex-row align-items-center"
      >
        <div>
          <BoxLogo />
        </div>
        <h2 className="mb-0">ShipShap</h2>
      </a>
      {props.organizations?.length > 1 && organization ? (
        <Dropdown className={"org-select"}>
          <p className={"mb-1"}>Organizations:</p>
          <Dropdown.Toggle>
            <p className={"mb-0"}>
              {organization.business_name || organization.id}
            </p>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {props.organizations.map((org) => {
              return (
                <Dropdown.Item
                  key={org.id}
                  onClick={() => {
                    props.setApplicationContext(
                      {
                        organizationId: org.id,
                      },
                      () => {
                        if (org.id === organization.id) {
                          return;
                        }
                        const searchParams = new URLSearchParams(search);
                        if (searchParams.has("org_id")) {
                          searchParams.delete("org_id");
                        }
                        window.location.replace(
                          `${pathname}?${searchParams.toString()}`,
                        );
                      },
                    );
                  }}
                >
                  {org.business_name || org.id}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
      <div className="nav-list d-flex flex-column col-12 p-0">
        <NavItem
          name={"Overview"}
          icon={<OverviewIcon />}
          link={
            isAllowedRedirect(ROUTES.DASHBOARD)
              ? redirectURL(ROUTES.DASHBOARD)
              : ROUTES.DASHBOARD
          }
          isExternal={isAllowedRedirect(ROUTES.DASHBOARD)}
        />
        <NavItem
          name={"Orders"}
          icon={<OrdersIcon />}
          link={
            isAllowedRedirect(ROUTES.ORDERS)
              ? redirectURL(ROUTES.ORDERS)
              : ROUTES.ORDERS
          }
          isExternal={isAllowedRedirect(ROUTES.ORDERS)}
        />
        <NavItem
          name={"Labels"}
          icon={<LabelsIcon />}
          link={
            isAllowedRedirect(ROUTES.LABEL)
              ? redirectURL(ROUTES.LABEL)
              : ROUTES.LABEL
          }
          isExternal={isAllowedRedirect(ROUTES.LABEL)}
        />
        <NavItem
          name={"Manifests"}
          icon={<FiPackage />}
          link={
            isAllowedRedirect(ROUTES.MANIFESTS)
              ? redirectURL(ROUTES.MANIFESTS)
              : ROUTES.MANIFESTS
          }
          isExternal={isAllowedRedirect(ROUTES.MANIFESTS)}
        />
        <NavItem
          name={"Stores"}
          icon={<FiShoppingCart />}
          link={
            isAllowedRedirect(ROUTES.STORES)
              ? redirectURL(ROUTES.STORES)
              : ROUTES.STORES
          }
          isExternal={isAllowedRedirect(ROUTES.STORES)}
        />
        {!hideWallet && (
          <NavItem
            name={"Wallets"}
            icon={<LuWallet />}
            link={
              isAllowedRedirect(ROUTES.WALLETS)
                ? redirectURL(ROUTES.WALLETS)
                : ROUTES.WALLETS
            }
            isExternal={isAllowedRedirect(ROUTES.WALLETS)}
          />
        )}
      </div>
      <div className="bottom d-flex flex-column col-12 p-0">
        <div className="">
          {/*<li>*/}
          {/*    <div className={`toggler nav-item col-12 d-flex flex-row align-items-center `} onClick={()=>switchCookies(cookies.is_test)}>*/}
          {/*        <div className="d-flex flex-row align-items-center">*/}
          {/*            <TestGear/>*/}
          {/*            <p className="mb-0">Testing Mode</p>*/}
          {/*        </div>*/}
          {/*        <div className={`gutter align-items-center justify-content-center ml-auto `}>*/}
          {/*            <div className="slider"></div>*/}
          {/*        </div>*/}
          {/*    </div>*/}
          {/*</li>*/}

          <NavItem
            name={"Help Center"}
            icon={<HelpIcon />}
            link={
              isAllowedRedirect(ROUTES.HELP_CENTER)
                ? redirectURL(ROUTES.HELP_CENTER)
                : ROUTES.HELP_CENTER
            }
            isExternal={isAllowedRedirect(ROUTES.HELP_CENTER)}
          />
          <NavItem
            name={"Settings"}
            icon={<SettingIcon />}
            link={ROUTES.ACCOUNT}
            missing={missingDetails(props.details)}
          />
        </div>
        {props.context.auth === FIREBASE_AUTH && (
          <div
            className="store d-flex flex-column align-items-center justify-content-center"
            onClick={() => {
              props.clearSelectedCheckout(() => {
                history.push(ROUTES.STORES + "?connect=true");
              });
            }}
          >
            <StoreIcon />
            <a>
              <button>Connect a Store</button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({
  businessDetails,
  applicationContext,
  userDetails,
}) => ({
  details: businessDetails.details,
  context: applicationContext.context,
  userInfo: userDetails.info,
  organizations: userDetails.organizations,
});

export default connect(mapStateToProps, {
  clearSelectedCheckout,
  setApplicationContext,
})(Sidebar);
